import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMyTicket, useReply } from '../api/hooks';

export function TicketPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: ticket, isLoading } = useMyTicket(id);
  const reply = useReply(id ?? '');
  const [body, setBody] = useState('');

  if (isLoading || !ticket) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  async function send() {
    if (!body.trim()) return;
    await reply.mutateAsync({ body, isInternal: false });
    setBody('');
  }

  return (
    <Box>
      <Button onClick={() => navigate('/tickets')} sx={{ mb: 1 }}>
        ← Back to my tickets
      </Button>
      <Typography variant="h1" sx={{ mb: 0.5 }}>
        #{ticket.ticketNumber} · {ticket.subject}
      </Typography>
      <Chip label={ticket.status} color="primary" sx={{ mb: 2 }} />

      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
            {ticket.description}
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h2" sx={{ mb: 1 }}>
        Updates
      </Typography>
      <Stack spacing={1.5} sx={{ mb: 3 }}>
        {ticket.comments.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            No updates yet.
          </Typography>
        )}
        {ticket.comments.map((c) => (
          <Card key={c.id} variant="outlined">
            <CardContent sx={{ py: 1.5 }}>
              <Typography variant="subtitle2">{c.authorName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {new Date(c.createdAt).toLocaleString()}
              </Typography>
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5 }}>
                {c.body}
              </Typography>
            </CardContent>
          </Card>
        ))}
      </Stack>

      <Card variant="outlined">
        <CardContent>
          <TextField
            label="Add a reply"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
          {reply.isError && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Failed to send your reply.
            </Alert>
          )}
          <Box sx={{ textAlign: 'right', mt: 1 }}>
            <Button variant="contained" onClick={() => void send()} disabled={reply.isPending || !body.trim()}>
              Send
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
